import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconBellFill18 } from "nucleo-ui-fill-18"
import { IconBellOutline18, IconCheckOutline18 } from "nucleo-ui-outline-18"

import type { SubscribableEntityType } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { Avatar, IconButton, Popover, Tooltip } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { generateId } from "@/lib/id"
import { useRole } from "@/lib/useRole"
import { mutateOk, useAuthData, useZero } from "@/lib/zero"
import { NOTIFICATIONS_COPY as copy } from "@/screens/app/notifications/copy"
import { useSubscriptionToggle } from "./subscribe-button"
import {
	orderSubscribers,
	subscriberSourceLabel,
	subscribersSentence,
} from "./subscribers-sentence"

interface Props {
	entityType: SubscribableEntityType
	entityId: string
}

interface MemberPickerProps extends Props {
	selfId: string
	isSelfSubscribed: boolean
	sourceByUserId: ReadonlyMap<string, string>
}

/** Workspace members, you first, checked when they follow; picking one adds or removes them. */
function MemberPicker({
	entityType,
	entityId,
	selfId,
	isSelfSubscribed,
	sourceByUserId,
}: MemberPickerProps) {
	const zero = useZero()
	const [users] = useQuery(queries.users())
	const members = useMemo(
		() =>
			[...users].sort(
				(a, b) => Number(b.id === selfId) - Number(a.id === selfId),
			),
		[users, selfId],
	)

	const setSubscribed = async (userId: string, isSubscribed: boolean) => {
		try {
			await mutateOk(
				zero.mutate(
					mutators.notificationSubscriptions.set({
						id: generateId(),
						entityType,
						entityId,
						isSubscribed,
						...(userId !== selfId && { userId }),
					}),
				),
				"server",
			)
		} catch {
			toast.error({ title: copy.toasts.failed })
		}
	}

	return (
		<Command>
			<CommandInput autoFocus placeholder={copy.subscribe.searchPeople} />
			<CommandList className="max-h-[min(60dvh,320px)]">
				<CommandEmpty>{copy.subscribe.noPeople}</CommandEmpty>
				<CommandGroup>
					{members.map((user) => {
						const name = user.name ?? user.email
						const isSelf = user.id === selfId
						const isChecked = isSelf
							? isSelfSubscribed
							: sourceByUserId.has(user.id)
						const source = isChecked ? sourceByUserId.get(user.id) : undefined
						return (
							<CommandItem
								key={user.id}
								value={`${name} ${user.email}`}
								onSelect={() => void setSubscribed(user.id, !isChecked)}
							>
								<div className="flex w-full items-center gap-2">
									<Avatar
										size="xs"
										roundness="full"
										name={name}
										image={user.imageUrl}
									/>
									<span className="flex-1 truncate">
										{isSelf ? copy.subscribe.you : name}
									</span>
									{source && (
										<span className="flex-none text-xs text-tertiary">
											{subscriberSourceLabel(source)}
										</span>
									)}
									<IconCheckOutline18
										className={cn(
											"h-4 w-4 flex-none",
											isChecked ? "opacity-100" : "opacity-0",
										)}
									/>
								</div>
							</CommandItem>
						)
					})}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

/** Bell filled while you follow. Members pick who gets updates; viewers toggle only themselves. */
export function SubscribersButton({ entityType, entityId }: Props) {
	const { userId } = useAuthData()
	const { role } = useRole()
	const [open, setOpen] = useState(false)
	const { isSubscribed, toggle } = useSubscriptionToggle(entityType, entityId)
	const [rows] = useQuery(queries.notificationSubscribers({ entityId }))
	const subscribers = useMemo(
		() => orderSubscribers(rows, userId),
		[rows, userId],
	)
	const sourceByUserId = useMemo(
		() => new Map(subscribers.map((s) => [s.userId, s.source])),
		[subscribers],
	)
	const Icon = isSubscribed ? IconBellFill18 : IconBellOutline18
	const sentence = subscribersSentence(subscribers, userId)

	if (role === "VIEWER") {
		return (
			<Tooltip label={sentence}>
				<IconButton
					variant="ghost"
					aria-label={
						isSubscribed ? copy.subscribe.unsubscribe : copy.subscribe.subscribe
					}
					aria-pressed={isSubscribed}
					Icon={Icon}
					iconClassName="transform-gpu"
					onClick={() => void toggle()}
				/>
			</Tooltip>
		)
	}

	return (
		<Popover
			className="w-72 p-0"
			align="end"
			open={open}
			onOpenChange={setOpen}
			triggerTooltip={sentence}
			content={
				<MemberPicker
					entityType={entityType}
					entityId={entityId}
					selfId={userId}
					isSelfSubscribed={isSubscribed}
					sourceByUserId={sourceByUserId}
				/>
			}
		>
			<IconButton
				variant="ghost"
				aria-label={copy.subscribe.subscribers}
				Icon={Icon}
				iconClassName="transform-gpu"
			/>
		</Popover>
	)
}
